import * as React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { cn } from "../lib/utils";

/**
 * Sélecteur d'heure format 24h basé sur deux menus déroulants (heures + minutes).
 *
 * Pourquoi pas <input type="time"> ou un champ texte ?
 *   - Sur les formulaires de planification (Planning collaborateurs, Agenda
 *     départements), l'utilisateur veut sélectionner rapidement une heure
 *     sans avoir à taper les 4 chiffres. Deux listes déroulantes 24h/60min
 *     rendent la saisie plus fiable, cohérente cross-navigateur, et
 *     empêchent les valeurs invalides.
 *
 * Props :
 *   - value        : string "HH:MM" (ou vide)
 *   - onChange(v)  : appelée avec la valeur formatée "HH:MM"
 *   - minuteStep   : pas (défaut 5). Si `value` a une minute hors du pas
 *                    (ex. "09:07"), cette valeur est ajoutée dynamiquement
 *                    à la liste pour ne pas être perdue.
 *   - disabled, className, data-testid : relayés au wrapper.
 *
 * data-testid enfants :
 *   - {testid}-hour   : trigger du select heures
 *   - {testid}-minute : trigger du select minutes
 */
const TimePicker24 = React.forwardRef(function TimePicker24(
  { value, onChange, minuteStep = 5, disabled = false, className, ...props },
  ref,
) {
  const testId = props["data-testid"];
  const [hVal, mVal] = React.useMemo(() => {
    const parts = String(value || "").split(":");
    const h = /^\d{1,2}$/.test(parts[0] || "")
      ? String(Math.min(23, parseInt(parts[0], 10))).padStart(2, "0")
      : "";
    const m = /^\d{1,2}$/.test(parts[1] || "")
      ? String(Math.min(59, parseInt(parts[1], 10))).padStart(2, "0")
      : "";
    return [h, m];
  }, [value]);

  const hours = React.useMemo(
    () => Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")),
    [],
  );

  const minutes = React.useMemo(() => {
    const step = Math.max(1, Math.min(30, minuteStep | 0));
    const list = [];
    for (let i = 0; i < 60; i += step) list.push(String(i).padStart(2, "0"));
    if (mVal && !list.includes(mVal)) {
      list.push(mVal);
      list.sort();
    }
    return list;
  }, [minuteStep, mVal]);

  const emit = (hh, mm) => {
    if (!onChange) return;
    onChange(`${hh}:${mm}`);
  };

  const handleHour = (v) => emit(v, mVal || "00");
  const handleMin = (v) => emit(hVal || "00", v);

  return (
    <div
      ref={ref}
      className={cn("flex items-center gap-1", className)}
      data-testid={testId}
    >
      <Select value={hVal} onValueChange={handleHour} disabled={disabled}>
        <SelectTrigger
          className="h-9 px-2 text-sm flex-1 min-w-0 font-mono"
          aria-label="Heures"
          data-testid={testId ? `${testId}-hour` : undefined}
        >
          <SelectValue placeholder="HH" />
        </SelectTrigger>
        <SelectContent className="max-h-[260px]">
          {hours.map((hh) => (
            <SelectItem
              key={hh}
              value={hh}
              className="text-sm font-mono"
              data-testid={testId ? `${testId}-hour-opt-${hh}` : undefined}
            >
              {hh}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-zinc-500 font-mono text-sm select-none px-0.5" aria-hidden="true">
        :
      </span>
      <Select value={mVal} onValueChange={handleMin} disabled={disabled}>
        <SelectTrigger
          className="h-9 px-2 text-sm flex-1 min-w-0 font-mono"
          aria-label="Minutes"
          data-testid={testId ? `${testId}-minute` : undefined}
        >
          <SelectValue placeholder="MM" />
        </SelectTrigger>
        <SelectContent className="max-h-[260px]">
          {minutes.map((mm) => (
            <SelectItem
              key={mm}
              value={mm}
              className="text-sm font-mono"
              data-testid={testId ? `${testId}-minute-opt-${mm}` : undefined}
            >
              {mm}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
});

export default TimePicker24;
